import { useCallback, useEffect, useRef, useState } from 'react';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import {
  mapRevitOperationSnapshotToProgressModel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';

export interface WorkflowAvailabilityProgressCopy {
  title: string;
  message: string;
  detail?: string;
  completeMessage?: string;
  errorMessage?: string;
}

/**
 * Delay before a config-edit re-check fires. Gives typing time to settle so
 * the check does not run (and spam Revit) on every keystroke.
 */
export const AVAILABILITY_RECHECK_DEBOUNCE_MS = 700;

export interface BeginAvailabilityCheckOptions {
  /**
   * Silent checks never surface the modal progress window. Used for re-checks
   * triggered by field edits: the modal steals focus on mount, which blurs
   * the active combobox and closes its dropdown mid-typing.
   */
  silent?: boolean;
}

function buildInitialProgressModel(copy: WorkflowAvailabilityProgressCopy): OperationProgressModel {
  return {
    title: copy.title,
    message: copy.message,
    detail: copy.detail,
    status: 'running',
    percent: null,
    locked: true,
  };
}

export function useWorkflowAvailabilityProgress(copy: WorkflowAvailabilityProgressCopy) {
  const copyRef = useRef(copy);
  const mountedRef = useRef(true);
  const checkIdRef = useRef(0);
  const silentCheckIdsRef = useRef<Set<number>>(new Set());
  const pendingEndTimersRef = useRef<Array<ReturnType<typeof globalThis.setTimeout>>>([]);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityProgress, setAvailabilityProgress] = useState<OperationProgressModel | null>(null);

  useEffect(() => {
    copyRef.current = copy;
  }, [copy]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      pendingEndTimersRef.current.forEach((timerId) => globalThis.clearTimeout(timerId));
      pendingEndTimersRef.current = [];
    };
  }, []);

  const beginAvailabilityCheck = useCallback((options?: BeginAvailabilityCheckOptions): number => {
    const checkId = checkIdRef.current + 1;
    checkIdRef.current = checkId;

    if (options?.silent) {
      silentCheckIdsRef.current.add(checkId);
      return checkId;
    }

    if (mountedRef.current) {
      setIsCheckingAvailability(true);
      setAvailabilityProgress(buildInitialProgressModel(copyRef.current));
    }

    return checkId;
  }, []);

  const updateAvailabilityProgressFromHost = useCallback((
    checkId: number,
    progress: RevitOperationProgressSnapshot
  ) => {
    if (!mountedRef.current || checkIdRef.current !== checkId || silentCheckIdsRef.current.has(checkId)) {
      return;
    }

    setAvailabilityProgress((current) => {
      const defaults = copyRef.current;
      return mapRevitOperationSnapshotToProgressModel(progress, {
        title: current?.title ?? defaults.title,
        message: defaults.message,
        detail: defaults.detail,
        completeMessage: defaults.completeMessage,
        errorMessage: defaults.errorMessage,
        locked: true,
      });
    });
  }, []);

  const endAvailabilityCheck = useCallback((checkId: number): void => {
    const wasSilent = silentCheckIdsRef.current.delete(checkId);
    if (!mountedRef.current || checkIdRef.current !== checkId || wasSilent) {
      return;
    }

    const timerId = globalThis.setTimeout(() => {
      pendingEndTimersRef.current = pendingEndTimersRef.current.filter((pendingId) => pendingId !== timerId);
      if (!mountedRef.current || checkIdRef.current !== checkId) {
        return;
      }

      setIsCheckingAvailability(false);
    }, 0);
    pendingEndTimersRef.current.push(timerId);
  }, []);

  return {
    isCheckingAvailability,
    availabilityProgress,
    beginAvailabilityCheck,
    updateAvailabilityProgressFromHost,
    endAvailabilityCheck,
  };
}
